import { useTranslation } from 'react-i18next';

import type { BracketStatus, ChessChallengeStatus, TournamentStatus } from '@/types/qchess';
import { cn } from '@/lib/cn';

// Tone per status — reuses the pinned QLASH semantic tokens. Tournament,
// challenge and bracket statuses are merged into one map; overlapping keys
// (in_progress) share an entry.
const TONE: Record<TournamentStatus | ChessChallengeStatus | BracketStatus, string> = {
  // Tournament
  created: 'bg-bg-tertiary text-fg-muted',
  started: 'bg-success/15 text-success',
  finished: 'bg-fg-muted/15 text-fg-muted',
  // Challenge
  pending: 'bg-warning/15 text-warning',
  in_progress: 'bg-success/15 text-success',
  completed: 'bg-brand/15 text-brand',
  declined: 'bg-danger/15 text-danger',
  expired: 'bg-fg-muted/15 text-fg-muted',
  aborted: 'bg-danger/15 text-danger',
  // Bracket
  registration: 'bg-warning/15 text-warning',
  ready: 'bg-brand/15 text-brand',
  complete: 'bg-brand/15 text-brand',
  cancelled: 'bg-danger/15 text-danger',
};

interface ChessStatusBadgeProps {
  status: TournamentStatus | ChessChallengeStatus | BracketStatus;
  /** i18n key prefix in the chess namespace. Tournaments reuse the filter
   *  labels; challenges use challenges.status.<status>; brackets use
   *  bracket.status.<status>. */
  variant?: 'tournament' | 'challenge' | 'bracket';
  className?: string;
}

export function ChessStatusBadge({
  status,
  variant = 'tournament',
  className,
}: ChessStatusBadgeProps) {
  const { t } = useTranslation('chess');
  const label =
    variant === 'challenge'
      ? t(`challenges.status.${status}`)
      : variant === 'bracket'
        ? t(`bracket.status.${status}`)
        : t(`tournaments.filter.${status}`);
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide',
        TONE[status] ?? 'bg-bg-tertiary text-fg-muted',
        className,
      )}
    >
      {label}
    </span>
  );
}
